import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface MaintenanceSettings {
  maintenance_mode: boolean;
  maintenance_message: string;
}

interface UseMaintenanceModeReturn {
  isMaintenanceMode: boolean;
  message: string;
  isLoading: boolean;
  refetch: () => Promise<void>;
}

export function useMaintenanceMode(): UseMaintenanceModeReturn {
  const [settings, setSettings] = useState<MaintenanceSettings>({
    maintenance_mode: false,
    maintenance_message: "Estamos otimizando o sistema. Pode haver instabilidade temporária.",
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("system_settings")
        .select("maintenance_mode, maintenance_message")
        .eq("id", "global")
        .single();

      if (error) {
        console.error("Error fetching maintenance settings:", error);
        return;
      }

      if (data) {
        setSettings({
          maintenance_mode: data.maintenance_mode ?? false,
          maintenance_message: data.maintenance_message ?? "Estamos otimizando o sistema. Pode haver instabilidade temporária.",
        });
      }
    } catch (err) {
      console.error("Error in useMaintenanceMode:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();

    // Subscribe to realtime changes
    const channel = supabase
      .channel("system_settings_changes")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "system_settings",
          filter: "id=eq.global",
        },
        (payload) => {
          const newData = payload.new as MaintenanceSettings;
          setSettings({
            maintenance_mode: newData.maintenance_mode ?? false,
            maintenance_message: newData.maintenance_message ?? "Estamos otimizando o sistema. Pode haver instabilidade temporária.",
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    isMaintenanceMode: settings.maintenance_mode,
    message: settings.maintenance_message,
    isLoading,
    refetch: fetchSettings,
  };
}
